import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';

const APIProviderFormModal = ({ show, handleClose, mode, providerData, onSave }) => {
  const [formData, setFormData] = useState({
    serviceName: '',
    providerName: '',
    providerType: 'Online',
    status: 'Active',
    credentials: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceTypes = [
    'Recharge',
    'Bill Payment',
    'Money Transfer',
    'AEPS',
    'Pan Card',
    'Fastag',
    'DMT',
    'BBPS',
    'Insurance'
  ];

  // Load provider data when editing
  useEffect(() => {
    if (mode === 'edit' && providerData) {
      setFormData({
        serviceName: providerData.serviceName || '',
        providerName: providerData.providerName || '',
        providerType: providerData.providerType || 'Online',
        status: providerData.status || 'Active',
        credentials: providerData.credentials || ''
      });
    } else if (mode === 'add') {
      // Reset form for add mode
      setFormData({
        serviceName: '',
        providerName: '',
        providerType: 'Online',
        status: 'Active',
        credentials: ''
      });
    }
    setErrors({});
  }, [mode, providerData, show]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    // Service Name validation
    if (!formData.serviceName) {
      newErrors.serviceName = 'Service name is required';
    }

    // Provider Name validation
    if (!formData.providerName.trim()) {
      newErrors.providerName = 'Provider name is required';
    } else if (formData.providerName.trim().length < 3) {
      newErrors.providerName = 'Provider name must be at least 3 characters';
    }

    // Credentials validation
    if (!formData.credentials.trim()) {
      newErrors.credentials = 'Credentials are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const dataToSave = {
      ...formData,
      providerName: formData.providerName.trim(),
      credentials: formData.credentials.trim()
    };

    // If editing, include the ID
    if (mode === 'edit' && providerData) {
      dataToSave.id = providerData.id;
    }

    try {
      await onSave(dataToSave);
      handleModalClose();
    } catch (error) {
      console.error('Error saving provider:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setFormData({
      serviceName: '',
      providerName: '',
      providerType: 'Online',
      status: 'Active',
      credentials: ''
    });
    setErrors({});
    setIsSubmitting(false);
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={`bi ${mode === 'add' ? 'bi-plus-circle' : 'bi-pencil-square'} me-2`}></i>
          {mode === 'add' ? 'Add New API Provider' : 'Edit API Provider'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            {/* Service Name Field */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Service Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="serviceName"
                  value={formData.serviceName}
                  onChange={handleChange}
                  isInvalid={!!errors.serviceName}
                  disabled={isSubmitting}
                >
                  <option value="">Select Service</option>
                  {serviceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.serviceName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* API Provider Name Field */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  API Provider Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="providerName"
                  value={formData.providerName}
                  onChange={handleChange}
                  placeholder="Enter provider name"
                  isInvalid={!!errors.providerName}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.providerName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Online/Offline Provider Field */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Online/Offline Provider <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="providerType"
                  value={formData.providerType}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="Online">Online</option>
                  <option value="Offline">Offline</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Status Field */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Status <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Credentials Field */}
            <Col md={12} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Credentials <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="credentials"
                  value={formData.credentials}
                  onChange={handleChange}
                  placeholder="Enter API credentials (API Key, Token, etc.)"
                  isInvalid={!!errors.credentials}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.credentials}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Enter API credentials like API Key, Token, Secret Key, etc.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer style={{ borderTop: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
          <Button 
            type="submit"
            disabled={isSubmitting}
            className="btn-outline-primary-custom"
          >
            {isSubmitting ? 'Saving...' : mode === 'add' ? 'Add Provider' : 'Update Provider'}
          </Button>
          <Button 
            variant="secondary" 
            onClick={handleModalClose}
            disabled={isSubmitting}
            className="btn-outline-danger-custom"
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default APIProviderFormModal;

